import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  getRemediationActions,
  getRemediationRules,
  createRemediationRule,
  updateRuleStatus,
  getRemediationExecutions,
  executeRemediation,
} from '@/api/modelStudioApi';
import { getServices } from '@/api/api';
import { Shield, Play, Pause, CheckCircle, XCircle, AlertTriangle, Clock, Zap } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const RemediationManagement = () => {
  const [actions, setActions] = useState([]);
  const [rules, setRules] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [services, setServices] = useState([]);
  // Anomaly detection removed from remediation management
  const [loading, setLoading] = useState(true);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showExecutionDialog, setShowExecutionDialog] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);

  // Rule form state
  const [ruleForm, setRuleForm] = useState({
    name: '',
    serviceName: 'all',
    metricName: 'all',
    condition: 'anomaly_score > 0.8',
    actions: [],
    autoExecute: false,
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [actionsData, rulesData, executionsData, servicesData] = await Promise.all([
        getRemediationActions(),
        getRemediationRules(),
        getRemediationExecutions(),
        getServices(),
      ]);

      setActions(actionsData);
      setRules(rulesData);
      setExecutions(executionsData);
      setServices(servicesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching remediation data:', error);
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    try {
      await createRemediationRule(ruleForm);
      setShowRuleDialog(false);
      setRuleForm({
        name: '',
        serviceName: 'all',
        metricName: 'all',
        condition: 'anomaly_score > 0.8',
        actions: [],
        autoExecute: false,
      });
      fetchData();
    } catch (error) {
      console.error('Error creating rule:', error);
    }
  };

  const handleToggleRule = async (ruleId, currentStatus) => {
    try {
      await updateRuleStatus(ruleId, !currentStatus);
      fetchData();
    } catch (error) {
      console.error('Error toggling rule:', error);
    }
  };

  const handleExecuteRemediation = async (anomalyId) => {
    try {
      const result = await executeRemediation(anomalyId);
      fetchData();
      alert(`Executed ${result.executions.length} remediation actions`);
    } catch (error) {
      console.error('Error executing remediation:', error);
    }
  };

  const getActionIcon = (actionType) => {
    const icons = {
      restart_service: '🔄',
      scale_up: '📈',
      scale_down: '📉',
      clear_cache: '🗑️',
      rollback: '⏮️',
      alert: '🔔',
      circuit_breaker: '⚡',
      adjust_timeout: '⏱️',
    };
    return icons[actionType] || '⚙️';
  };

  const getExecutionStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'executing':
        return <Play className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getExecutionStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'executing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="remediation-loading">
        <Shield className="w-12 h-12 animate-pulse text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="remediation-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Automated Remediation</h2>
          <p className="text-gray-600 mt-1">Configure and track automated remediation workflows</p>
        </div>
        <Button onClick={() => setShowRuleDialog(true)} data-testid="new-rule-btn">
          <Shield className="w-4 h-4 mr-2" />
          New Rule
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{actions.length}</div>
            <p className="text-xs text-gray-500 mt-1">Available actions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Rules</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {rules.filter((r) => r.is_active).length}
            </div>
            <p className="text-xs text-gray-500 mt-1">Out of {rules.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Executions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{executions.length}</div>
            <p className="text-xs text-gray-500 mt-1">
              {executions.filter((e) => e.status === 'completed').length} successful
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Success Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {executions.length > 0
                ? ((executions.filter((e) => e.status === 'completed').length / executions.length) * 100).toFixed(0)
                : 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">Execution success</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Library */}
      <Card>
        <CardHeader>
          <CardTitle>Action Library</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {actions.map((action) => (
              <div
                key={action.id}
                className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
                data-testid={`action-${action.id}`}
              >
                <div className="text-2xl">{getActionIcon(action.action_type)}</div>
                <div className="flex-1">
                  <div className="font-medium">{action.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{action.description}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      {action.action_type}
                    </Badge>
                    {action.is_active ? (
                      <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Inactive
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Remediation Rules */}
      <Card>
        <CardHeader>
          <CardTitle>Remediation Rules</CardTitle>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No remediation rules configured. Create one to automate responses!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  data-testid={`rule-${rule.id}`}
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        rule.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{rule.name}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {rule.service_name} / {rule.metric_name} • {rule.condition}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500">{rule.actions.length} actions</span>
                        {rule.auto_execute && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs">
                            <Zap className="w-3 h-3 mr-1" />
                            Auto-Execute
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant={rule.is_active ? 'destructive' : 'default'}
                    onClick={() => handleToggleRule(rule.id, rule.is_active)}
                  >
                    {rule.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Executions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Executions</CardTitle>
        </CardHeader>
        <CardContent>
          {executions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No executions yet. Rules will trigger automatically when anomalies are detected.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {executions.slice(0, 10).map((execution) => {
                const action = actions.find((a) => a.id === execution.action_id);
                return (
                  <div
                    key={execution.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setSelectedExecution(execution);
                      setShowExecutionDialog(true);
                    }}
                    data-testid={`execution-${execution.id}`}
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      {getExecutionStatusIcon(execution.status)}
                      <div className="flex-1">
                        <div className="font-medium">{action?.name || 'Unknown Action'}</div>
                        <div className="text-sm text-gray-500">
                          Anomaly: {execution.anomaly_id.substring(0, 8)}... •{' '}
                          {new Date(execution.started_at).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {execution.completed_at && (
                        <div className="text-xs text-gray-500">
                          Duration:{' '}
                          {Math.round(
                            (new Date(execution.completed_at) - new Date(execution.started_at)) / 1000
                          )}s
                        </div>
                      )}
                      <Badge className={getExecutionStatusColor(execution.status)}>
                        {execution.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Anomalies section removed - anomaly detection handled separately */}

      {/* Create Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="max-w-2xl" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle>Create Remediation Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium block mb-2">Rule Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="e.g., High Response Time Auto-Scale"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-2">Service</label>
                <Select
                  value={ruleForm.serviceName}
                  onValueChange={(value) => setRuleForm({ ...ruleForm, serviceName: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.name}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">Metric</label>
                <Select
                  value={ruleForm.metricName}
                  onValueChange={(value) => setRuleForm({ ...ruleForm, metricName: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Metrics</SelectItem>
                    <SelectItem value="avg_response_time">Avg Response Time</SelectItem>
                    <SelectItem value="request_count">Request Count</SelectItem>
                    <SelectItem value="error_rate">Error Rate</SelectItem>
                    <SelectItem value="cpu_usage">CPU Usage</SelectItem>
                    <SelectItem value="memory_usage">Memory Usage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Condition</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="e.g., anomaly_score > 0.8"
                value={ruleForm.condition}
                onChange={(e) => setRuleForm({ ...ruleForm, condition: e.target.value })}
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Actions to Execute</label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded">
                {actions.map((action) => (
                  <label
                    key={action.id}
                    className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={ruleForm.actions.includes(action.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRuleForm({ ...ruleForm, actions: [...ruleForm.actions, action.id] });
                        } else {
                          setRuleForm({
                            ...ruleForm,
                            actions: ruleForm.actions.filter((id) => id !== action.id),
                          });
                        }
                      }}
                    />
                    <span className="text-sm">{action.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="autoExecute"
                checked={ruleForm.autoExecute}
                onChange={(e) => setRuleForm({ ...ruleForm, autoExecute: e.target.checked })}
              />
              <label htmlFor="autoExecute" className="text-sm font-medium cursor-pointer">
                Enable Auto-Execute (automatically run actions when condition is met)
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowRuleDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateRule}
                disabled={!ruleForm.name || ruleForm.actions.length === 0}
                data-testid="create-rule-btn"
              >
                Create Rule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Execution Details Dialog */}
      <Dialog open={showExecutionDialog} onOpenChange={setShowExecutionDialog}>
        <DialogContent data-testid="execution-dialog">
          <DialogHeader>
            <DialogTitle>Execution Details</DialogTitle>
          </DialogHeader>
          {selectedExecution && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <Badge className={getExecutionStatusColor(selectedExecution.status)}>
                    {selectedExecution.status}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Action</div>
                  <div className="font-medium">
                    {actions.find((a) => a.id === selectedExecution.action_id)?.name}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Started At</div>
                  <div className="font-medium">{new Date(selectedExecution.started_at).toLocaleString()}</div>
                </div>
                {selectedExecution.completed_at && (
                  <div>
                    <div className="text-sm text-gray-600">Completed At</div>
                    <div className="font-medium">
                      {new Date(selectedExecution.completed_at).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              {selectedExecution.result && (
                <div>
                  <div className="text-sm text-gray-600 mb-2">Result</div>
                  <div className="p-3 bg-gray-50 rounded text-sm">
                    <pre>{JSON.stringify(selectedExecution.result, null, 2)}</pre>
                  </div>
                </div>
              )}

              {selectedExecution.error_message && (
                <div>
                  <div className="text-sm text-gray-600 mb-2">Error</div>
                  <div className="p-3 bg-red-50 rounded text-sm text-red-800">
                    {selectedExecution.error_message}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemediationManagement;
